import React, { useState, useEffect } from "react";
import SelectDropdown from "../../../components/elements/forms/Select";
import InterviewCandidatesTable from "../../candidates/dashboard[id]/InterviewTable";
import { useNavigate } from "react-router-dom";
import { useJobs } from "../hooks/useJobs";
import { useRecentInterviews } from "../hooks/useRecentInterviews";
import { ArrowLeft, ChevronLeft, ChevronRight } from "lucide-react";
import { Skeleton } from "antd";
import Table2 from "../../../components/Table";
import { convertCandidatesToCandidatesType } from "../../../utility/candidateHelper";
import { CandidateType } from "../../../store/types/candidate";

const columns2: {
  header: string;
  accessor: keyof CandidateType;
  sortable: boolean;
}[] = [
  { header: "S/N", accessor: "id", sortable: true },
  { header: "Name", accessor: "name", sortable: true },
  { header: "Email", accessor: "email", sortable: true },
  { header: "Job Title", accessor: "job", sortable: true },
  { header: "Interview Date", accessor: "date", sortable: true },
  { header: "Start Time", accessor: "interviewDate", sortable: true },
  { header: "Expires", accessor: "expirationDate", sortable: true },
  { header: "IV Status", accessor: "invitationStatus", sortable: true },
  { header: "Status", accessor: "recommendationStatus", sortable: true },
  { header: "Interview Status", accessor: "interviewStatus", sortable: true },
];

const InterviewsView: React.FC = () => {
  const navigate = useNavigate();
  const { jobs, loading: jobsLoading, error: jobsError } = useJobs();
  const {
    interviews: recentInterviews,
    loading: recentLoading,
    error: recentError,
    pagination,
    fetchRecentInterviews,
  } = useRecentInterviews();

  const [selectedJobId, setSelectedJobId] = useState<string>("");

  const handleJobChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedJobId(e.target.value);
  };

  const handleAddNewJob = () => {
    navigate("/create-job");
  };

  const handleClearSelection = () => {
    setSelectedJobId("");
  };

  const handlePageChange = (page: number) => {
    fetchRecentInterviews(page, pagination.pageSize);
  };

  const handleRowClick = (row: CandidateType) => {
    navigate(
      `/reports?interviewId=${row.interviewId}&candidateId=${row.candidateId}`,
    );
  };

  // Convert recent interviews to table format using best practices by mapping to ICandidate structure
  const convertedRecentData = convertCandidatesToCandidatesType(
    recentInterviews
      .map((item) => ({
        candidate: {
          id: item.candidate.id,
          name: item.candidate.name,
          email: item.candidate.email,
          phoneNumber: item.candidate.phoneNumber,
        },
        interviewId: item.id,
        interviewDate: item.interviewDate,
        expirationDate: item.expirationDate,
        jobTitle: item.job?.interviewTemplates?.[0]?.title || "N/A",
        isInvite: item.isInvited,
        interviewStatus: item.status,
        recommendationStatus: "N/A",
      }))
      .sort(
        (a, b) =>
          new Date(b.interviewDate).getTime() -
          new Date(a.interviewDate).getTime(),
      ) as any,
  );

  return (
    <div className="w-full py-5 max-w-[1096px] mx-auto px-3">
      <div className="pb-1 md:mr-8 mr-5 flex items-center gap-4 border-b border-border text-20 mb-5">
        <div
          onClick={() => history.back()}
          className="text-blue-600 hover:text-blue-800 cursor-pointer"
        >
          <ArrowLeft className="w-6 h-6" />
        </div>
        <p className="font-[600]">
          {selectedJobId ? "Job Interviews" : "Recent Interviews"}
        </p>
      </div>

      <div className="flex items-center gap-3 mb-4">
        <div className="flex-1">
          <SelectDropdown
            items={jobs?.map((job) => ({
              id: job.id,
              name: job.currentTemplate.title,
            }))}
            name="jobId"
            label="Filter by Job"
            value={selectedJobId}
            onChange={handleJobChange}
            disabled={jobsLoading}
          />
          {jobsError && (
            <p className="text-red-500 text-sm mt-1">{jobsError}</p>
          )}
        </div>

        {selectedJobId && (
          <button
            className="h-[45px] px-4 mt-[15px] flex items-center justify-center bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
            onClick={handleClearSelection}
          >
            Clear Filter
          </button>
        )}

        <button
          className="h-[45px] w-[150px] mt-[15px] flex items-center justify-center bg-secondary text-white rounded-lg hover:bg-secondary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          onClick={handleAddNewJob}
          disabled={jobsLoading}
        >
          Add New Job
        </button>
      </div>

      {/* Show job-specific interviews or recent interviews */}
      {selectedJobId ? (
        <InterviewCandidatesTable key={selectedJobId} jobId={selectedJobId} />
      ) : (
        <div className="w-full">
          <div className="mb-4">
            <p className="text-lg font-[600]">Recent Interviews</p>
            <p className="text-sm text-gray-600">
              {recentLoading
                ? "Loading..."
                : `${pagination.totalItems} interview(s) found`}
            </p>
          </div>

          {recentLoading ? (
            <Skeleton active paragraph={{ rows: 6 }} />
          ) : recentError ? (
            <div className="text-center py-12 bg-red-50 rounded-lg border border-red-200">
              <p className="text-red-500">{recentError}</p>
            </div>
          ) : convertedRecentData.length > 0 ? (
            <>
              <Table2<CandidateType>
                data={convertedRecentData}
                columns={columns2}
                onRowClick={handleRowClick}
              />

              {/* Pagination */}
              {pagination.totalPages > 1 && (
                <div className="flex items-center justify-between mt-4 px-4">
                  <p className="text-sm text-gray-600">
                    Page {pagination.currentPage} of {pagination.totalPages}
                  </p>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() =>
                        handlePageChange(pagination.currentPage - 1)
                      }
                      disabled={pagination.currentPage === 1 || recentLoading}
                      className="px-3 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-1"
                    >
                      <ChevronLeft className="w-4 h-4" />
                      Previous
                    </button>
                    <button
                      onClick={() =>
                        handlePageChange(pagination.currentPage + 1)
                      }
                      disabled={
                        pagination.currentPage === pagination.totalPages ||
                        recentLoading
                      }
                      className="px-3 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-1"
                    >
                      Next
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-12 bg-gray-50 rounded-lg border border-gray-200">
              <p className="text-gray-500">No recent interviews found</p>
              <p className="text-sm text-gray-400 mt-1">
                Interviews will appear here once they are conducted
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default InterviewsView;
